import { Ref, ref, computed } from "vue";
import { EventPriceDto } from "@/models.g";
import {
  PublicPurchaseServiceViewModel,
  TicketPurchaseDtoViewModel,
} from "@/viewmodels.g";
import { cartQuantityChanged } from "@common/analytics";
import { TicketInput } from "@/models.g";

export function useSeatingTicketChange(
  purchase: Ref<TicketPurchaseDtoViewModel>,
  service: PublicPurchaseServiceViewModel,
) {
  const updatingSeatedGATickets = ref(new Map<string, boolean>());

  function setupSeatedTicketChange() {
    service.setTicketQuantity.setConcurrency?.("debounce");
  }

  function getGeneralAdmissionPayload(
    generalAdmissionToBeAddedList: {
      eventPrice: EventPriceDto;
      quantity: number;
    }[],
  ) {
    return generalAdmissionToBeAddedList.map((eventPriceWithQuantity) => {
      const ticket = new TicketInput();
      ticket.quantity = eventPriceWithQuantity.quantity;
      ticket.eventPriceId = eventPriceWithQuantity.eventPrice.id;
      return ticket;
    });
  }

  async function addGeneralAdmissionTicket(
    seat: string,
    generalAdmissionToBeAddedList: {
      eventPrice: EventPriceDto;
      quantity: number;
    }[],
  ) {
    if (generalAdmissionToBeAddedList.length > 0) {
      const generalAdmissionPayload = getGeneralAdmissionPayload(
        generalAdmissionToBeAddedList,
      );

      try {
        service.addMultipleTickets.args = {
          publicId: purchase.value.publicId,
          tickets: generalAdmissionPayload,
          seat: seat,
        };

        await service.addMultipleTickets.invokeWithArgs();

        // Analytics tracking
        generalAdmissionToBeAddedList.forEach((eventPriceWithQuantity) => {
          cartQuantityChanged(
            purchase.value,
            eventPriceWithQuantity.eventPrice,
            eventPriceWithQuantity.quantity,
          );
        });
      } catch (error) {
        console.log(error);
        throw error; // Re-throw so caller can handle
      }
    }
  }

  async function removeGeneralAdmissionTicket(
    seat: string,
    generalAdmissionToBeRemovedList: {
      eventPrice: EventPriceDto;
      quantity: number;
    }[],
  ) {
    if (generalAdmissionToBeRemovedList.length > 0) {
      const ticketIdList: number[] = [];

      for (const item of generalAdmissionToBeRemovedList) {
        // There is a bug fix for https://dev.azure.com/Hagadone/Ticketing/_boards/board/t/Ticketing%20MAP/Stories?workitem=2532
        // When filtering from the eventPrice, you need to also excluding the one that is NOT GA.
        const filteredEventPrice =
          purchase.value.tickets?.filter((ticket) => {
            return (
              ticket.eventPriceId === item.eventPrice.id &&
              ticket.seatNumber?.includes(seat!)
            );
          }) || [];
        for (let i = 0; i < item.quantity; i++) {
          ticketIdList.push(filteredEventPrice![i].ticketId!);
        }
      }
      try {
        service.removeMultipleTickets.args = {
          publicId: purchase.value.publicId,
          ticketIdList: ticketIdList,
        };
        await service.removeMultipleTickets.invokeWithArgs();

        // Analytics tracking
        generalAdmissionToBeRemovedList.forEach((eventPriceWithQuantity) => {
          cartQuantityChanged(
            purchase.value,
            eventPriceWithQuantity.eventPrice,
            -eventPriceWithQuantity.quantity,
          );
        });
      } catch (error) {
        console.log(error);
        throw error; // Re-throw so caller can handle
      }
    }
  }

  async function reloadPurchase() {
    const ticketPurchaseDtoModel = new TicketPurchaseDtoViewModel();
    const updatedData = await ticketPurchaseDtoModel.$load(
      purchase.value.publicId!,
    );
    await purchase.value.$loadCleanData(updatedData);
  }

  /**
   * Helper to reload purchase and seating info after a ticket change.
   */
  async function reloadPurchaseAndSeating() {
    // Reload purchase and seating info in a consistent way
    await reloadPurchase();
    await service.getSeating(purchase.value.publicId);
  }

  /**
   * Combined handler for updating GA cart (add/remove, reload, reset).
   */
  async function onUpdateGACart(
    seat: string,
    generalAdmissionToBeAddedList: {
      eventPrice: EventPriceDto;
      quantity: number;
    }[],
    generalAdmissionToBeRemovedList: {
      eventPrice: EventPriceDto;
      quantity: number;
    }[],
  ) {
    try {
      await addGeneralAdmissionTicket(seat, generalAdmissionToBeAddedList);
      await removeGeneralAdmissionTicket(seat, generalAdmissionToBeRemovedList);
      await reloadPurchaseAndSeating();
    } catch (error) {
      console.error("Error updating GA cart:", error);
      try {
        await reloadPurchaseAndSeating();
      } catch {
        // ignore secondary reload failure
      }
      throw error;
    }
  }

  function getCurrentQuantity(
    eventPriceId: number,
    seatNumber?: string,
  ): number {
    const tickets = purchase.value.tickets || [];
    return tickets.filter((t) => {
      const matchesPrice = t.eventPriceId === eventPriceId;
      const matchesSeat = seatNumber
        ? t.seatNumber === seatNumber
        : !t.isReservedIndividualSeat;
      return matchesPrice && matchesSeat;
    }).length;
  }

  /**
   * Update quantity immediately without confirm button
   */
  async function updateGeneralAdmissionQuantityImmediate(
    eventPrice: EventPriceDto,
    newQuantity: number,
    seatNumber: string,
  ): Promise<void> {
    const currentQuantity = getCurrentQuantity(eventPrice.id!, seatNumber);

    if (newQuantity === currentQuantity) return;

    try {
      if (newQuantity > currentQuantity) {
        // Add tickets
        await addGeneralAdmissionTicket(seatNumber, [
          {
            eventPrice,
            quantity: newQuantity - currentQuantity,
          },
        ]);
      } else {
        // Remove tickets
        await removeGeneralAdmissionTicket(seatNumber, [
          {
            eventPrice,
            quantity: currentQuantity - newQuantity,
          },
        ]);
      }
    } catch (error) {
      console.error("Failed to update quantity:", error);
      // Don't show alert for immediate updates, let the UI handle it
      throw error;
    }
  }

  function isUpdatingSeatedGA(
    eventPriceId?: number,
    seatNumber?: string,
  ): boolean {
    if (!eventPriceId || !seatNumber) return false;
    const key = `${eventPriceId}-${seatNumber}`;
    return updatingSeatedGATickets.value.get(key) || false;
  }

  // memoized lookup for event prices
  const eventPriceMap = computed(() => {
    const map = new Map<number, EventPriceDto>();
    purchase.value.eventDate?.eventPrices?.forEach((ep) => {
      map.set(ep.id!, ep);
    });
    return map;
  });

  /**
   * Handle seated GA quantity update with local quantity tracking
   */
  async function handleSeatedGAQuantityUpdate(
    eventPriceId: number,
    input: number | string,
    localQuantity: number,
    seatNumber: string,
    refreshLocalQuantity?: () => void,
  ) {
    const key = `${eventPriceId}-${seatNumber}`;
    updatingSeatedGATickets.value.set(key, true);

    let num = parseInt(input.toString());
    const oldLocalQuantity = localQuantity;
    const eventPrice = eventPriceMap.value.get(eventPriceId)!;
    const minimumQuantity = eventPrice.minimumQuantity ?? 0;
    const maximumPurchaseQuantity = eventPrice.maximumPurchaseQuantity!;

    if (isNaN(num)) {
      num = 0;
    } else if (num < minimumQuantity) {
      num = num < oldLocalQuantity ? 0 : minimumQuantity;
    } else if (maximumPurchaseQuantity && num > maximumPurchaseQuantity) {
      num = maximumPurchaseQuantity;
    }

    try {
      await updateGeneralAdmissionQuantityImmediate(
        eventPrice,
        num,
        seatNumber,
      );

      cartQuantityChanged(purchase.value, eventPrice, num - oldLocalQuantity);

      // Reload to get updated data
      await reloadPurchaseAndSeating();
    } catch (err) {
      console.error("Failed to update seated GA quantity:", err);
      if (refreshLocalQuantity) {
        refreshLocalQuantity();
      }
    } finally {
      updatingSeatedGATickets.value.set(key, false);
    }
  }

  return {
    addGeneralAdmissionTicket,
    removeGeneralAdmissionTicket,
    getGeneralAdmissionPayload,
    getCurrentQuantity,
    updateGeneralAdmissionQuantityImmediate,
    reloadPurchaseAndSeating,
    reloadPurchase,
    handleSeatedGAQuantityUpdate,
    isUpdatingSeatedGA,
    updatingSeatedGATickets,
    setupSeatedTicketChange,
    onUpdateGACart,
  };
}
